import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Shield, BarChart3, Zap, Globe, 
  CheckCircle, ArrowRight, Play, ChevronRight,
  Building2, Ship, HardHat, Brain
} from 'lucide-react';
import './Home.css';

const Home: React.FC = () => {
  const [activeUSP, setActiveUSP] = useState(0);

  const uspItems = [
    {
      title: 'Dynamic Workflow Engine',
      points: ['Easily configure workflows without coding', 'Adapt processes for SOP, training, audits', 'Low consulting dependency'],
      icon: <Zap size={32} />
    },
    {
      title: 'Form Builder for Customization',
      points: ['Drag-and-drop form designer', 'Custom fields, validations, and layouts', 'Per client requirements'],
      icon: <FileText size={32} />
    },
    {
      title: 'Domain-Agnostic Flexibility',
      points: ['Supports pharma, biotech, MedTech', 'Configurable templates for GMP, GLP, GCP', 'Beyond life sciences'],
      icon: <Globe size={32} />
    },
    {
      title: 'AI-Powered Features',
      points: ['Smart document classification', 'Predictive compliance alerts', 'Automated impact analysis'],
      icon: <Brain size={32} />
    },
    {
      title: 'Integration Ready',
      points: ['Connect with ERP, LIMS, CRM', 'API-first approach', 'Regulatory portal connectivity'],
      icon: <BarChart3 size={32} />
    },
    {
      title: 'Regulatory Compliance',
      points: ['21 CFR Part 11, EU Annex 11 ready', 'GDPR, ISO 9001 compliant', 'Built-in audit trails & e-signatures'],
      icon: <Shield size={32} />
    },
  ];

  const services = [
    {
      icon: <FileText size={40} />,
      title: 'QC-Metric Platform',
      description: 'Comprehensive digital transformation platform with Content Management, Training Management, Quality Management, and Workflow-based Forms - all integrated seamlessly.'
    },
    {
      icon: <Shield size={40} />,
      title: 'Secure Hosting & Managed Services',
      description: 'Hosted in compliance-ready environments with high availability, automated backups and SOC-style operational controls.'
    },
    {
      icon: <Zap size={40} />,
      title: 'End-to-end IT Services',
      description: 'System integration, migration, validation and custom development delivered with GxP-aware processes and traceable evidence.'
    },
  ];

  const industries = [
    {
      icon: <Building2 size={48} />,
      title: 'Life Sciences',
      description: 'Pharma, Biotech, Medical Devices and CROs',
      link: '/industries#life-sciences'
    },
    {
      icon: <Ship size={48} />,
      title: 'Marine',
      description: 'Fleet operations and maintenance records',
      link: '/industries#marine'
    },
    {
      icon: <HardHat size={48} />,
      title: 'Mining',
      description: 'Safety workflows and environmental reporting',
      link: '/industries#mining'
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <span className="badge animate-fadeIn">Empowering Regulated Industries</span>
            <h1 className="hero-title animate-fadeIn">
              Empowering Regulated Industries through{' '}
              <span className="text-gradient">Digital Transformation</span>
            </h1>
            <p className="hero-subtitle animate-fadeIn">
              Where Innovation Meets Compliance, and Technology Drives Growth. 
              Shaping the future with compliant, intelligent, and scalable digital solutions 
              for Life Sciences and adjacent sectors.
            </p>
            <div className="hero-ctas animate-fadeIn">
              <Link to="/demo" className="btn btn-primary">
                Book a Demo
                <ArrowRight size={18} />
              </Link>
              <a href="#products" className="btn btn-outline-white">
                <Play size={18} />
                Explore Products
              </a>
            </div>
            <p className="hero-trust animate-fadeIn">
              <CheckCircle size={16} />
              Validated to global standards (GxP, FDA 21 CFR Part 11, ISO, GDPR, HIPAA)
            </p>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section" id="services">
        <div className="container">
          <div className="section-header">
            <span className="badge">What We Do</span>
            <h2>Enterprise-Grade Solutions for Regulated Industries</h2>
            <p>
              We deliver platforms and managed services that make compliance auditable, 
              processes measurable, and quality decisions timely.
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card card">
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <Link to="/products/qc-metric" className="service-link">
                  Learn More <ChevronRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="section section-alt" id="products">
        <div className="container">
          <div className="section-header">
            <span className="badge">Key Platform Features</span>
            <h2>What Makes QC-Metric Unique</h2>
            <p>
              Discover the powerful features that set QC-Metric apart as your unified quality management platform.
            </p>
          </div>
          <div className="usp-container">
            <div className="usp-tabs">
              {uspItems.map((item, index) => (
                <button
                  key={index}
                  className={`usp-tab ${activeUSP === index ? 'active' : ''}`}
                  onClick={() => setActiveUSP(index)}
                >
                  <span className="usp-tab-icon">{item.icon}</span>
                  <span className="usp-tab-title">{item.title}</span>
                </button>
              ))}
            </div>
            <div className="usp-content">
              <div className="usp-card">
                <div className="usp-card-icon">{uspItems[activeUSP].icon}</div>
                <h3>{uspItems[activeUSP].title}</h3>
                <ul className="usp-points">
                  {uspItems[activeUSP].points.map((point, index) => (
                    <li key={index}>
                      <CheckCircle size={18} />
                      {point}
                    </li>
                  ))}
                </ul>
                <Link to="/demo" className="btn btn-primary">
                  See Example <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="badge">Industries</span>
            <h2>Tailored Solutions for Your Sector</h2>
            <p>
              We serve regulated industries with specialized solutions designed for their unique compliance needs.
            </p>
          </div>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <Link key={index} to={industry.link} className="industry-card card">
                <div className="industry-icon">{industry.icon}</div>
                <h3>{industry.title}</h3>
                <p>{industry.description}</p>
                <span className="industry-link">
                  Learn More <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Quality Management?</h2>
            <p>
              See QC-Metric in action. Book a short 30–45 minute walkthrough tailored 
              to your industry and compliance needs.
            </p>
            <div className="cta-buttons">
              <Link to="/demo" className="btn btn-primary">
                Book a Demo <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
